"use client";
import { useTranslations } from "next-intl";

import React from "react";
import Image from "next/image";
import { User, Loader2 } from "lucide-react";
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets";
import { cn, formatPrice } from "@/lib/utils";
import type { SellerCheckoutInfo, SellerGroup } from "./types";
import { CartItemComponent } from "./cart-item";
import { SellerCheckoutNoteBanner } from "./seller-checkout-note-banner";
import { Button } from "@/components/ui/button";
import type { CheckoutGroupIdentity } from "@/lib/cart/checkout-groups";

interface Props {
  group: SellerGroup;
  /**
   * True when this seller owns MORE than one card in the current cart view —
   * i.e. their listings mix shipping modes, so they ship (and are paid for) as
   * separate orders. Drives the "Ships separately" hint; without it two cards
   * under one seller name look like a duplicate-render bug.
   */
  splitFromSameSeller?: boolean;
  isMobile: boolean;
  updateQuantityAction: (id: string, qty: number) => void;
  removeItemAction: (id: string) => void;
  updateShippingMethodAction: (id: string, shippingId: string) => void;
  refreshListingShippingMethodsAction?: (listingId: string) => void | Promise<void>
  activeSwipeItemId: string | null;
  setActiveSwipeItemIdAction: (id: string | null) => void;
  availability: Record<string, number>;
  controlsDisabled?: boolean;
  sellerValidationError?: string;
  /**
   * Which SURFACE this group is painted on — see the long note on
   * `CartItemComponentProps.onSheet` in `./cart-item.tsx`, which this forwards
   * verbatim. `true` = overlay panel (`--sheet*` ladder), `false` = page /
   * desktop-drawer surface (`bg-card` / `border-border`). Was `dark`.
   * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md §1, §4 (step 2c)
   */
  onSheet?: boolean;
  compact?: boolean;
  // Per-checkout-group props
  checkoutInfo?: SellerCheckoutInfo;
  onCheckout?: (group: CheckoutGroupIdentity) => void;
  // In-flight reservation tracking (a `CheckoutGroupKey` or null)
  creatingReservationsFor?: string | null;
  userId?: string | null;
  onLogin?: () => void;
  onRegister?: () => void;
}

export function SellerGroupComponent({
  group,
  splitFromSameSeller = false,
  isMobile,
  updateQuantityAction,
  removeItemAction,
  updateShippingMethodAction,
  refreshListingShippingMethodsAction,
  activeSwipeItemId,
  setActiveSwipeItemIdAction,
  availability,
  controlsDisabled,
  sellerValidationError,
  onSheet = false,
  compact = false,
  checkoutInfo,
  onCheckout,
  creatingReservationsFor = null,
  userId,
  onLogin,
  onRegister,
}: Props) {
  const t = useTranslations("sellerGroup");
  // Is THIS group's checkout in progress? Compare the canonical group key
  // (never a hand-rebuilt one) so a mixed-mode seller's two cards spin
  // independently instead of both reacting to either one's reservation.
  const isCreatingReservations = Boolean(
    creatingReservationsFor && creatingReservationsFor === group.checkoutGroup.key,
  );
  const isLiveRateGroup = group.checkoutGroup.shippingMode === "live_rate";
  // Text roles collapsed: both former branches now name the SAME token, so the
  // surface no longer decides them. Only the two rules below still differ.
  const nameCls = "font-semibold text-foreground";
  const iconCls = "text-muted-foreground";
  const sellerNameSize = compact ? "text-[15px]" : "";
  const labelCls = "text-muted-foreground";
  const valueCls = "text-foreground";
  const borderCls = onSheet ? "border-sheet-handle" : "border-border/30";

  return (
    <div className={cn("grid", compact ? "gap-2" : "gap-4")}>
      <div className={cn("flex items-center gap-2 py-2 border-b", onSheet && "border-sheet-handle")}>
        <div className={cn("rounded-full overflow-hidden bg-muted border-2 border-foreground/50 flex items-center justify-center", compact ? "relative w-7 h-7" : "relative w-8 h-8")}>
          {group.seller.profileImage ? (
            <Image
              // 28 px compact / 32 px regular -> SQUARE_SM. Sized at the render
              // site, not at the producer (`cart.tsx` builds `profileImage`),
              // because the same field name is reused by the order-email
              // payloads where a CDN-transform URL would be wrong.
              src={imagePresetUrl(group.seller.profileImage, SQUARE_SM)}
              alt={`Avatar of ${group.seller.name}`}
              fill
              sizes="32px"
              className="object-cover"
            />
          ) : (
            <User className={cn(compact ? "h-3.5 w-3.5" : "h-4 w-4", iconCls)} />
          )}
        </div>
        <h3 className={cn(nameCls, sellerNameSize)}>{group.seller.name}</h3>
        {/* Disambiguation for a seller split across two cards. Both markers are
            derived, never stored: the badge from the group's (homogeneous)
            shipping mode, the hint from how many groups this seller owns. */}
        {(isLiveRateGroup || splitFromSameSeller) && (
          <div className="ml-auto flex shrink-0 items-center gap-1.5">
            {isLiveRateGroup && (
              <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {t("liveRateBadge")}
              </span>
            )}
            {splitFromSameSeller && (
              <span className="text-[11px] italic text-muted-foreground">
                {t("shipsSeparately")}
              </span>
            )}
          </div>
        )}
      </div>
      {group.seller.checkoutNote?.trim() ? (
        <SellerCheckoutNoteBanner note={group.seller.checkoutNote} className="mt-3 mb-1" />
      ) : null}
      <ul className={cn("grid", compact ? "gap-0" : "gap-4")}>
        {group.items.map((item) => (
          <CartItemComponent
            key={item.id}
            item={item}
            isMobile={isMobile}
            onSheet={onSheet}
            compact={compact}
            updateQuantityAction={updateQuantityAction}
            removeItemAction={removeItemAction}
            updateShippingMethodAction={updateShippingMethodAction}
            refreshListingShippingMethodsAction={refreshListingShippingMethodsAction}
            activeSwipeItemId={activeSwipeItemId}
            setActiveSwipeItemIdAction={setActiveSwipeItemIdAction}
            availability={availability}
            controlsDisabled={!!controlsDisabled}
            sellerValidationError={sellerValidationError}
          />
        ))}
      </ul>

      {/* Per-seller checkout section */}
      {checkoutInfo && (
        <div className={cn("pt-3 mt-1 border-t", borderCls)}>
          {/* Seller totals */}
          <div className="space-y-1 text-sm mb-3">
            <div className={cn("flex justify-between", labelCls)}>
              <span>{t("subtotal")}</span>
              <span className={valueCls}>{formatPrice(checkoutInfo.subtotal, checkoutInfo.currencyCode)}</span>
            </div>
            <div className={cn("flex justify-between", labelCls)}>
              <span>{t("shipping")}</span>
              <span className={valueCls}>
                {!checkoutInfo.allShippingMethodsSelected ? "–" : checkoutInfo.shippingTotal === 0 ? t("free") : formatPrice(checkoutInfo.shippingTotal, checkoutInfo.currencyCode)}
              </span>
            </div>
            <div className={cn("flex justify-between", labelCls)}>
              <span>{t("serviceFee")}</span>
              <span className={valueCls}>{formatPrice(checkoutInfo.serviceFee, checkoutInfo.currencyCode)}</span>
            </div>
            <div className={cn("flex justify-between font-semibold pt-1", valueCls)}>
              <span>{t("total")}</span>
              <span>{formatPrice(checkoutInfo.total, checkoutInfo.currencyCode)}</span>
            </div>
          </div>

          {/* Checkout button or Login/Register for guests */}
          {!userId ? (
            <div className="space-y-2">
              <Button
                onClick={onLogin}
                className="w-full rounded-full font-semibold bg-foreground text-background hover:bg-foreground/90"
              >
                {t("logIn")}
              </Button>
              <Button
                onClick={onRegister}
                variant="outline"
                className={cn(
                  "w-full rounded-full font-semibold",
                  onSheet
                    ? "border-sheet-handle bg-muted/80 text-foreground hover:bg-sheet-handle"
                    : "border-border"
                )}
              >
                {t("register")}
              </Button>
            </div>
          ) : onCheckout && (
            <Button
              onClick={() =>
                onCheckout({
                  sellerId: checkoutInfo.sellerId,
                  currencyCode: checkoutInfo.currencyCode,
                  shippingMode: checkoutInfo.shippingMode,
                })
              }
              disabled={!checkoutInfo.canCheckout || isCreatingReservations}
              className="w-full rounded-full font-semibold bg-foreground text-background hover:bg-foreground/90"
            >
              {isCreatingReservations ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("reserving")}
                </>
              ) : (
                t("checkout", { price: formatPrice(checkoutInfo.total, checkoutInfo.currencyCode) })
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
